import { EmployerRule } from '../../../api/employer-rules'
import { FormValues } from './types'
import { dateToDateInputValue, dateInputToDateValue } from '../../../utils/date'
import { subHours } from 'date-fns'
import { pathStringToFormula } from './input-formula-utils'
import { InputFormula } from '../../../api/employer-rules'

/**
 * Transforms initial values for the form based on employer rule or creates default values
 */
export const transformInitialValues = (
  employerRule?: EmployerRule,
  employerId?: string,
): FormValues => {
  if (employerRule) {
    const hasSpecialPeriodsTypes =
      employerRule.specialPeriods && Array.isArray(employerRule.specialPeriods.types)
        ? employerRule.specialPeriods.types.length > 0
        : false

    return {
      ...employerRule,
      interval: {
        ...employerRule.interval,
        min: employerRule.interval.min ? employerRule.interval.min / (60 * 1000) : 0,
        max: employerRule.interval.max ? employerRule.interval.max / (60 * 1000) : 0,
        minPath: employerRule.interval.minPath || '',
        minFallback: employerRule.interval.minFallback
          ? employerRule.interval.minFallback / (60 * 1000)
          : '',
        maxPath: employerRule.interval.maxPath || '',
        maxFallback: employerRule.interval.maxFallback
          ? employerRule.interval.maxFallback / (60 * 1000)
          : '',
        minBoundsMode: employerRule.interval.minPath ? 'dynamic' : 'fixed',
        maxBoundsMode: employerRule.interval.maxPath ? 'dynamic' : 'fixed',
        mode:
          employerRule.interval.period === 'week'
            ? 'duration'
            : employerRule.interval.mode || 'duration',
        daysOfWeek:
          'daysOfWeek' in employerRule.interval && employerRule.interval.daysOfWeek
            ? employerRule.interval.daysOfWeek
            : [0, 1, 2, 3, 4, 5, 6],
        useCalendarDays:
          'useCalendarDays' in employerRule.interval
            ? employerRule.interval.useCalendarDays
            : false,
        // Handle inputFormula - it should already be in the correct format from backend
        inputFormula:
          'inputFormula' in employerRule.interval
            ? (employerRule.interval.inputFormula as InputFormula)
            : null,
      },
      valuesInTime: employerRule.valuesInTime.map(vt => ({
        value: vt.value,
        effectiveFrom: dateToDateInputValue(new Date(vt.effectiveFrom)),
        effectiveTo: vt.effectiveTo
          ? dateToDateInputValue(subHours(new Date(vt.effectiveTo), 4))
          : undefined,
      })),
      employerDataOptions: employerRule.employerDataOptions,
      // Initialize special periods filters from existing rule
      specialPeriods: employerRule.specialPeriods
        ? {
            types: employerRule.specialPeriods.types || [],
          }
        : {
            types: [],
          },
      // Enable the UI toggle when existing rule already has special periods configured
      useSpecialPeriodsFilters: hasSpecialPeriodsTypes,
    } as unknown as FormValues
  }

  // Default values for new rule
  return {
    name: '',
    employer: employerId || '',
    employerDataOptions: {
      setDurationAsProportional: false,
      addToWorkedTotal: false,
    },
    kind: 'add',
    valuesInTime: [
      {
        value: '',
        effectiveFrom: dateToDateInputValue(new Date()),
        effectiveTo: '',
      },
    ],
    interval: {
      name: '',
      period: '',
      inputFormula: null,
      min: '',
      max: '',
      minPath: '',
      minFallback: '',
      maxPath: '',
      maxFallback: '',
      minBoundsMode: 'fixed',
      maxBoundsMode: 'fixed',
      mode: 'duration',
      originOffset: undefined,
      daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
      useCalendarDays: false,
    },
    display: {
      summary: {
        totals: true,
        costs: true,
        days: true,
      },
      payroll: true,
    },
    // By default, special periods filters are disabled on new rule
    useSpecialPeriodsFilters: false,
    specialPeriods: {
      types: [],
    },
  } as unknown as FormValues
}

/**
 * Transforms form values to API payload format
 */
export const transformFormToPayload = (values: FormValues): any => {
  // Destructure to avoid sending UI-only fields like useSpecialPeriodsFilters directly
  const { interval, valuesInTime, specialPeriods, useSpecialPeriodsFilters, ...rest } = values

  const payload = {
    ...rest,
    interval: {
      ...interval,
      name: values.name,
      useCalendarDays: interval.useCalendarDays,
      daysOfWeek: interval.daysOfWeek,
      // Handle inputFormula - send to backend as-is if present
      inputFormula: interval.inputFormula || undefined,
      // Handle bounds based on individual modes
      // Min bounds
      ...(interval.minBoundsMode === 'fixed'
        ? {
            min: interval.min ? Number(interval.min) * 60 * 1000 : undefined,
          }
        : {
            minPath: interval.minPath || undefined,
            minFallback: interval.minFallback
              ? Number(interval.minFallback) * 60 * 1000
              : undefined,
          }),
      // Max bounds
      ...(interval.maxBoundsMode === 'fixed'
        ? {
            max: interval.max ? Number(interval.max) * 60 * 1000 : undefined,
          }
        : {
            maxPath: interval.maxPath || undefined,
            maxFallback: interval.maxFallback
              ? Number(interval.maxFallback) * 60 * 1000
              : undefined,
          }),
      originOffset:
        interval.period === 'week' && interval.originOffset?.type
          ? interval.originOffset.type === 'none'
            ? { type: 'fixed', value: 0 }
            : interval.originOffset.type === 'contract'
              ? { type: 'summaryPath', path: 'durations.contract.total' }
              : undefined
          : undefined,
    },
    valuesInTime: valuesInTime.map(vt => ({
      value: Number(vt.value),
      effectiveFrom: dateInputToDateValue(vt.effectiveFrom),
      effectiveTo: vt.effectiveTo ? dateInputToDateValue(vt.effectiveTo) : undefined,
    })),
    // Apply specialPeriods filters only when explicitly enabled in the UI
    specialPeriods: {
      types: useSpecialPeriodsFilters && specialPeriods?.types ? specialPeriods.types : [],
    },
  }

  // For timeRange mode, set a default inputFormula pointing to durations.accounted
  if (values.interval.mode === 'timeRange') {
    payload.interval.inputFormula = pathStringToFormula('durations.accounted')
  }

  return payload
}
